import React, { useContext, useEffect, useState, useRef } from 'react';
import { PrismButton, PrismDialog } from '@prism-ui/react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { TimeCommitmentModal } from '../TimeCommitment';
import ApplicationWindowContext from '../ApplicationContainer/ApplicationWindowContext';
import ApplicationHeader from '../ApplicationHeader.jsx';
import ApplicationTimeline from '../ChapterStep/ApplicationTimeline.jsx';
import SubmittedApp from '../SubmittedApp/index.jsx';
import RoleCard from './RoleCard.jsx';
import {
  APPLICANT_TYPE_MENTEE,
  APPLICANT_TYPE_MENTOR,
  APPLICANT_TYPE_COMMITTEE,
  APPLICATION_STATUS_ID_PENDING,
  APPLICATION_STATUS_ID_DRAFT,
  APPLICATION_STATUS_ID_SUBMITTED,
  APPLICATION_STATUS_ID_WITHDRAWN,
  APP_TYPE_ID_TO_STRING,
} from '../constants';
import { formatDate } from '../../../util/dateUtils';
import './Requirements.css';

const Requirements = ({
  application,
  saveApplication,
  applicantTypeId,
  setApplicantTypeId,
  jumpToStep,
  currentUser,
  selectedChapterId,
  refreshApplicationState,
  copyRatingsToNewTrack,
}) => {
  const thisPageStep = 2;

  // ==================== Context & Derived State ====================
  const {
    opening,
    hard_deadline,
    chapter_id: contextChapterId,
  } = useContext(ApplicationWindowContext);

  const chapter_id = selectedChapterId || application?.chapter_id || contextChapterId || currentUser?.chapter_id;

  // ==================== Local State ====================
  const [applicationWindows, setApplicationWindows] = useState([]);
  const [selectedChapterWindow, setSelectedChapterWindow] = useState(null);
  const [showTimeCommitmentModal, setShowTimeCommitmentModal] = useState(false);
  const [showTimeCommitmentModal_OKButton, setshowTimeCommitmentModal_OKButton] = useState(false);
  const [timeCommitmentApplicantType, setTimeCommitmentApplicantType] = useState(applicantTypeId);
  const [showConfirmWithdraw, setShowConfirmWithdraw] = useState(false);
  const [roleTenureSettings, setRoleTenureSettings] = useState({});
  const [chapters, setChapters] = useState([]);
  const [approvalTypeConfig, setApprovalTypeConfig] = useState(null);

  // ==================== Computed Values ====================
  const now = new Date();
  const isAfterOpening = opening < now;
  const isBeforeClosing = hard_deadline > now;

  const isSubmitted = application?.status_id === APPLICATION_STATUS_ID_SUBMITTED || false;
  const isWithdrawn = application?.status_id === APPLICATION_STATUS_ID_WITHDRAWN;
  const isInProgressStatus =
    application?.status_id === APPLICATION_STATUS_ID_PENDING ||
    application?.status_id === APPLICATION_STATUS_ID_DRAFT;
  const isPending = !isSubmitted && !isWithdrawn && (isInProgressStatus || applicantTypeId != null);
  const okToApply = !isPending && !isSubmitted && isAfterOpening && isBeforeClosing;
  const isApproved = application?.ManagerApproval?.approved === true;
  const windowIsOpen = isAfterOpening && isBeforeClosing;
  const canSwitchTrack = isPending && windowIsOpen;

  // Check if manager approval is required for a given applicant type
  const isApprovalRequired = (typeId) => {
    if (!approvalTypeConfig) return typeId === APPLICANT_TYPE_MENTEE; // default: only mentees
    if (typeId === APPLICANT_TYPE_MENTEE) return approvalTypeConfig.mentee_required;
    if (typeId === APPLICANT_TYPE_MENTOR) return approvalTypeConfig.mentor_required;
    if (typeId === APPLICANT_TYPE_COMMITTEE) return approvalTypeConfig.tcm_required;
    return false;
  };

  const getIsApplicantType = (type) => {
    const currentType = application?.applicant_type_id ?? applicantTypeId;
    return currentType === type;
  };

  const isMentor = getIsApplicantType(APPLICANT_TYPE_MENTOR);
  const isMentee = getIsApplicantType(APPLICANT_TYPE_MENTEE);
  const isCommittee = getIsApplicantType(APPLICANT_TYPE_COMMITTEE);

  // ==================== Effects ====================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const windowsResponse = await axios.get('/api/application_windows/all');
        setApplicationWindows(windowsResponse.data);
        
        const chaptersResponse = await axios.get('/api/chapters');
        setChapters(chaptersResponse.data);
      } catch (windowsError) {
        console.warn('Failed to fetch application windows:', windowsError);
        setApplicationWindows([]);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch role tenure settings and manager approval type config for the current chapter
    if (chapter_id) {
      axios
        .get(`/api/role_tenure/chapter/${chapter_id}`)
        .then((response) => {
          // Convert array to a map for easy lookup
          const tenureMap = {};
          response.data.forEach((item) => {
            tenureMap[item.applicant_type_id] = item.tenure_months;
          });
          setRoleTenureSettings(tenureMap);
        })
        .catch((err) => {
          console.warn('Could not fetch role tenure settings:', err);
          // Set default values
          setRoleTenureSettings({
            [APPLICANT_TYPE_MENTEE]: 24,
            [APPLICANT_TYPE_MENTOR]: 24,
            [APPLICANT_TYPE_COMMITTEE]: 24,
          });
        });

      axios
        .get(`/api/manager_approval_type_config/${chapter_id}`)
        .then((response) => setApprovalTypeConfig(response.data))
        .catch((err) => {
          console.warn('Could not fetch approval type config:', err);
          setApprovalTypeConfig({ mentee_required: true, mentor_required: false, tcm_required: false });
        });
    }
  }, [chapter_id]);

  useEffect(() => {
    const chapterWindow = applicationWindows.find(
      (window) => window.chapter_id === chapter_id
    );
    setSelectedChapterWindow(chapterWindow);
  }, [applicationWindows, chapter_id]);

  // ==================== Event Handlers ====================
  const onBack = () => {
    jumpToStep(thisPageStep - 1);
  };

  const startApplication = async (type) => {
    setShowTimeCommitmentModal(false);
    const currentType = application?.applicant_type_id ?? applicantTypeId;

    if (application?.id && application.status_id === APPLICATION_STATUS_ID_WITHDRAWN) {
      // Restarting from a withdrawn app: update the track and reactivate
      try {
        if (currentType && currentType !== type) {
          await copyRatingsToNewTrack(currentType, type);
        }
        // saveApplication auto-reactivates withdrawn apps (sets status to DRAFT)
        await saveApplication({ ...application, applicant_type_id: type });
        await refreshApplicationState();
      } catch (error) {
        console.error('Error reactivating application:', error);
      }
    } else if (application?.id && currentType && currentType !== type) {
      // Switching tracks on an in-progress app: carry the skill ratings over
      try {
        await copyRatingsToNewTrack(currentType, type);
        await saveApplication({ ...application, applicant_type_id: type });
        await refreshApplicationState();
      } catch (error) {
        console.error('Error switching track:', error);
        return;
      }
    }

    setApplicantTypeId(type);
    jumpToStep(thisPageStep + 1);
  };

  const withdrawApplication = async () => {
    if (!application?.id) {
      setApplicantTypeId(null);
      setShowConfirmWithdraw(false);
      return;
    }
    try {
      await axios.post(`/api/applications/${application.id}/withdraw`);
      setApplicantTypeId(null);
      setShowConfirmWithdraw(false);
      await refreshApplicationState();
    } catch (error) {
      console.error('Error withdrawing application:', error);
      setShowConfirmWithdraw(false);
    }
  };

  // The track only changes once the user accepts the new track's time commitments
  // (see startApplication), so cancelling the modal leaves the current track intact.
  const initiateTrackSwitch = (newType) => {
    setshowTimeCommitmentModal_OKButton(false);
    setTimeCommitmentApplicantType(newType);
    setShowTimeCommitmentModal(true);
  };

  const onCancelHandler = () => {
    setShowTimeCommitmentModal(false);
  };

  // Helper function to format tenure requirement text
  const getTenureText = (applicantTypeId) => {
    const months = roleTenureSettings[applicantTypeId];
    if (months === undefined) return '';
    if (months === 0) return 'No tenure requirement';
    if (months === 1) return '1 month with the company is desired';
    return `${months} months with the company is desired`;
  };

  // Helper function to get chapter name
  const getChapterName = () => {
    const chapter = chapters.find(c => c.id === chapter_id);
    return chapter ? chapter.location : '';
  };

  // ==================== UI Components ====================
  const timeCommitButton = (
    <button
      type="button"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        textDecoration: 'underline',
        color: 'var(--color-primary)',
        cursor: 'pointer',
      }}
      onClick={() => {
        setshowTimeCommitmentModal_OKButton(true);
        setShowTimeCommitmentModal(true);
      }}
    >
      Time Commitments
    </button>
  );

  const roleCardProps = [
    {
      title: 'Mentor',
      text: (
        <>
          <div style={{ minHeight: '140px' }}>
            <p style={{ fontStyle: 'italic', marginBottom: '0.75rem' }}>Provides guidance and experience with their Mentee</p>
            <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
              <li>Must be at least an Engineer/Architect&nbsp;5</li>
              <li>Must accept {timeCommitButton}</li>
            </ul>
          </div>
          {roleTenureSettings[APPLICANT_TYPE_MENTOR] !== undefined && (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)', marginTop: '0.5rem' }}>{getTenureText(APPLICANT_TYPE_MENTOR)}</p>
          )}
        </>
      ),
      roleText: 'Mentor',
      isPending,
      isSubmitted,
      windowIsOpen,
      okToApply,
      canSwitchTrack,
      isApproved: !isApprovalRequired(APPLICANT_TYPE_MENTOR) || isApproved,
      approvedText: 'Application Submitted',
      isRole: isMentor,
      onBegin: () => initiateTrackSwitch(APPLICANT_TYPE_MENTOR),
      onWithdraw: () => setShowConfirmWithdraw(true),
      onContinue: () => jumpToStep(thisPageStep + 1),
    },
    {
      title: 'Mentee',
      text: (
        <>
          <div style={{ minHeight: '140px' }}>
            <p style={{ fontStyle: 'italic', marginBottom: '0.75rem' }}>Brings a project from inception to demonstration</p>
            <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
              <li>Must accept {timeCommitButton}</li>
              <li>Manager must approve your participation</li>
            </ul>
          </div>
          {roleTenureSettings[APPLICANT_TYPE_MENTEE] !== undefined && (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)', marginTop: '0.5rem' }}>{getTenureText(APPLICANT_TYPE_MENTEE)}</p>
          )}
        </>
      ),
      roleText: 'Mentee',
      isPending,
      isSubmitted,
      windowIsOpen,
      okToApply,
      canSwitchTrack,
      isApproved: !isApprovalRequired(APPLICANT_TYPE_MENTEE) || isApproved,
      approvedText: 'Application Submitted',
      isRole: isMentee,
      onBegin: () => initiateTrackSwitch(APPLICANT_TYPE_MENTEE),
      onWithdraw: () => setShowConfirmWithdraw(true),
      onContinue: () => jumpToStep(thisPageStep + 1),
    },
    {
      title: 'Committee Member',
      text: (
        <>
          <div style={{ minHeight: '140px' }}>
            <p style={{ fontStyle: 'italic', marginBottom: '0.75rem' }}>Support Mentors and Mentees on their CLEAR Journey</p>
            <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
              <li>Must accept {timeCommitButton}</li>
            </ul>
          </div>
          {roleTenureSettings[APPLICANT_TYPE_COMMITTEE] !== undefined && (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)', marginTop: '0.5rem' }}>{getTenureText(APPLICANT_TYPE_COMMITTEE)}</p>
          )}
        </>
      ),
      roleText: 'Committee',
      isPending,
      isSubmitted,
      windowIsOpen,
      okToApply,
      canSwitchTrack,
      isApproved: !isApprovalRequired(APPLICANT_TYPE_COMMITTEE) || isApproved,
      approvedText: 'Application Submitted',
      isRole: isCommittee,
      onBegin: () => initiateTrackSwitch(APPLICANT_TYPE_COMMITTEE),
      onWithdraw: () => setShowConfirmWithdraw(true),
      onContinue: () => jumpToStep(thisPageStep + 1),
    },
  ];

  const filteredRoleCardProps = isSubmitted
    ? roleCardProps.filter((card) => card.isRole)
    : roleCardProps;

  // ==================== Render ====================
  return (
    <>
      {isSubmitted ? (
        <div className="app-section" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.6rem',
            backgroundColor: 'var(--color-success)',
            // Fixed white text on saturated accent backgrounds throughout this block.
            color: '#fff',
            borderRadius: '2rem',
            padding: '0.4rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            marginBottom: '1rem',
          }}>
            ✓ Application Submitted
          </div>
          <h3 style={{ margin: '0.5rem 0 0.25rem', fontSize: '1.15rem', color: 'var(--color-text)' }}>
            {(() => {
              const typeMap = { 1: 'Mentee', 2: 'Mentor', 3: 'Committee Member' };
              return typeMap[application?.applicant_type_id] || 'Applicant';
            })()}
          </h3>
          {!isApproved && isApprovalRequired(application?.applicant_type_id) && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: 'var(--color-primary)',
              color: '#fff',
              borderRadius: '2rem',
              padding: '0.3rem 1rem',
              fontSize: '0.82rem',
              marginTop: '0.5rem',
            }}>
              Awaiting Manager Approval
            </div>
          )}

          {selectedChapterWindow && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--color-border)',
              fontSize: '0.88rem',
              color: 'var(--color-text-faded)',
            }}>
              <span>
                <strong style={{ color: 'var(--color-success)' }}>Opens:</strong>{' '}
                {formatDate(selectedChapterWindow.opening)}
              </span>
              <span>
                <strong style={{ color: 'var(--color-danger)' }}>Closes:</strong>{' '}
                {formatDate(selectedChapterWindow.soft_deadline)}
              </span>
              <span>
                <strong style={{ color: 'var(--color-primary)' }}>Results by:</strong>{' '}
                {formatDate(selectedChapterWindow.notification_date)}
              </span>
            </div>
          )}

          <div style={{ marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={() => setShowConfirmWithdraw(true)}
              style={{
                backgroundColor: 'var(--color-danger-dk)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Withdraw Application
            </button>
          </div>
        </div>
      ) : (
        <>
        
        <div className="requirements" style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px' }}>
            <ApplicationHeader
              heading={`Program Eligibility Guidelines${!isSubmitted && getChapterName() ? ` — ${getChapterName()}` : ''}`}
              message={
                <p style={{ textAlign: 'left', display: 'inline-block', marginTop: '1rem' }}>
                  {chapter_id === 4
                    ? <>You must have received a <em>PDP 3</em> or above in the last rating to apply.</>
                    : <>You must have received an <em>Effective</em> or <em>Outstanding</em> rating on your last performance review and have not been on corrective action for the last twelve months.</>
                  }
                </p>
              }
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: '2rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {filteredRoleCardProps.map((props) => (
              <RoleCard key={props.title} {...props} />
            ))}
          </div>
        
          <TimeCommitmentModal
            show={showTimeCommitmentModal}
            onCancel={onCancelHandler}
            applicationType={timeCommitmentApplicantType}
            handleSubmit={() => startApplication(timeCommitmentApplicantType)}
            showOkButton={isSubmitted || showTimeCommitmentModal_OKButton}
          />
        </div>
        </>
      )}

      <PrismDialog
        open={showConfirmWithdraw}
        setAriaLabel="Withdraw confirmation"
        close-method="user"
        onPrismClose={() => setShowConfirmWithdraw(false)}
      >
        <span slot="header">Are you sure?</span>
        <p>
          Your application data will be preserved. You can select a new track
          and continue where you left off while the window is still open.
        </p>
        <div slot="footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <button
            type="button"
            className="btn-withdraw"
            onClick={withdrawApplication}
            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
          >
            Yes, withdraw it
          </button>
          <button
            type="button"
            className="btn-secondary-action"
            onClick={() => setShowConfirmWithdraw(false)}
            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
          >
            No, keep it
          </button>
        </div>
      </PrismDialog>


      {isSubmitted && (
        <div
          style={{
            border: '1px solid var(--color-border)',
            padding: '1em',
            marginTop: '3em',
            borderRadius: '5px',
          }}
        >
          <ApplicationHeader
            heading="Your Submission"
            message="Details of your submitted application are shown below. You will be notified of the status of your application after the application deadline."
          />
          <SubmittedApp
            params={{ application_id: application.id }}
            currentUser={currentUser}
            hideHeader
          />
        </div>
      )}

      {!isSubmitted && (
        <div
          style={{
            marginTop: '3rem',
            marginBottom: '2rem',
            textAlign: 'center',
          }}
        >
          <PrismButton
            tone="primary-brand"
            size="lg"
            onPrismClick={onBack}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </PrismButton>
        </div>
      )}
    </>
  );
};

export default Requirements;